import { action } from '@ember/object';
import type Owner from '@ember/owner';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import didUpdate from 'webapp/modifiers/did-update';
import FormInputComponent from 'webapp/components/form/input';
import FormRadiobuttonGroupComponent from 'webapp/components/form/radiobutton-group';

import type Block from 'webapp/models-custom/block';
import type BlockSetup from 'webapp/models-custom/block-setup';
import type VisualizationHeatmap from 'webapp/models-custom/visualization/heatmap';

interface Args {
	blockM: Block;
}

const CustomChoice = 'custom';
type ChoiceType = typeof CustomChoice | '';

export default class BlockSettingsItemsInLineComponent extends Component<Args> {
	@tracked showCustomInput = false;
	@tracked itemsInLine: number | null = null;

	options = [
		{
			value: '',
			label: 'Auto',
		},
		{
			value: CustomChoice,
			label: 'Custom',
		},
	];

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.updateThisFromModel();
	}

	get block(): Block {
		return this.args.blockM;
	}

	get blockSetup(): BlockSetup {
		return this.block.blockSetup;
	}

	get visualization(): VisualizationHeatmap {
		return this.blockSetup.visualization as VisualizationHeatmap;
	}

	get selected(): ChoiceType {
		return this.itemsInLine === null ? '' : CustomChoice;
	}

	@action
	changeSelected(value: AnySimpleValue): void {
		const selected = value as ChoiceType;
		this.showCustomInput = selected === CustomChoice;
		if (selected === '') {
			this.updateModelFromThis(null);
		} else if (!this.itemsInLine) {
			const itemsInLine = this.visualization.itemsInLineFromGranulation;
			this.itemsInLine = itemsInLine;
			this.updateModelFromThis(itemsInLine);
		}
	}

	@action
	changeItemsInLine(): void {
		const value = this.itemsInLine;
		let numericValue: number | null = null;
		if (value && !isNaN(+value)) {
			numericValue = +value;
		}
		this.itemsInLine = numericValue;
		this.updateModelFromThis(numericValue);
	}

	// ----- updating to / from model -----

	updateModelFromThis(itemsInLine: number | null): void {
		const block = this.block;
		block.itemsInLine = itemsInLine;
		block.blockSetup.saveBlock();
	}

	@action
	updateThisFromModel(): void {
		this.itemsInLine = this.block.itemsInLine || null;
		this.showCustomInput = this.selected === CustomChoice;
	}
	<template>
		<div class="row" {{didUpdate this.updateThisFromModel @blockM}}>
			<label># of items per line</label>
			<FormRadiobuttonGroupComponent
				class="radiobutton-switcher"
				@name="aggregation"
				@selected={{this.selected}}
				@options={{this.options}}
				@onChange={{this.changeSelected}}
				@theme="dark-blue"
			/>

			{{#if this.showCustomInput}}
				<FormInputComponent
					@loseFocusOnEnter={{true}}
					@value={{this.itemsInLine}}
					@focusOut={{this.changeItemsInLine}}
					@hideFloatLabel={{true}}
					placeholder="Items in line"
					@theme="dark"
				/>
			{{/if}}

		</div>
	</template>
}
